import { useState, useEffect } from "react";
import { getAppByChainId } from "../libs/Env";
import { toast } from "react-toastify";
import {
  useAppKitNetwork,
  useAppKitProvider,
  useAppKitAccount,
} from "@reown/appkit/react";
import { Contract, ethers } from "ethers";

export const useMigrationContract = () => {
  const [migrationData, setMigrationData] = useState({
    balanceTokenV1: "0",
    balanceStakingCompoundV1: "0",
    balanceStakingManualV1: "0",
    totalBalance: "0",
    isMigrated: false,
    totalTokenMigrated: "0",
    tokenAddressV1: "",
    tokenAddressV2: "",
    migrationWallet: ""
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { chainId } = useAppKitNetwork();
  const { address, isConnected } = useAppKitAccount();
  const { walletProvider } = useAppKitProvider("eip155");

  // Get migration contract instance
  const getMigrationContract = (withSigner = false) => {
    const currentChainId = chainId || 56; // Default to BSC mainnet if no chainId
    const config = getAppByChainId(currentChainId);
    if (!config.MIGRATION_ADDRESS || !config.MIGRATION_ABI) return null;

    // If wallet is not connected, use JsonRpcProvider for read-only operations
    if (!walletProvider && !withSigner) {
      // Use default RPC URLs for different chains
      const rpcUrls = {
        56: "https://bsc-dataseed1.binance.org/", // BSC Mainnet
        97: "https://data-seed-prebsc-1-s1.binance.org:8545/", // BSC Testnet
        137: "https://polygon-rpc.com/", // Polygon
        1337: "http://localhost:8545", // Local
      };
      
      const rpcUrl = config.RPC_URL || rpcUrls[currentChainId] || rpcUrls[56]; // Default to BSC mainnet
      const jsonRpcProvider = new ethers.JsonRpcProvider(rpcUrl);
      return new Contract(
        config.MIGRATION_ADDRESS,
        config.MIGRATION_ABI,
        jsonRpcProvider
      );
    }

    const ethersProvider = new ethers.BrowserProvider(walletProvider);
    const contractProvider = withSigner ? ethersProvider.getSigner() : ethersProvider;
    
    return new Contract(
      config.MIGRATION_ADDRESS,
      config.MIGRATION_ABI,
      contractProvider
    );
  };

  // Load migration data for connected user
  const loadMigrationData = async () => {
    if (!address || !isConnected) {
      setMigrationData({
        balanceTokenV1: "0",
        balanceStakingCompoundV1: "0",
        balanceStakingManualV1: "0",
        totalBalance: "0",
        isMigrated: false,
        totalTokenMigrated: "0",
        tokenAddressV1: "",
        tokenAddressV2: "",
        migrationWallet: ""
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const contract = getMigrationContract();
      if (!contract) {
        throw new Error("Migration contract not available for this network");
      }

      // Fetch user's migration data
      const [
        balanceTokenV1,
        balanceStakingCompoundV1,
        balanceStakingManualV1,
        isMigrated,
        totalTokenMigrated,
        tokenAddressV1,
        tokenAddressV2,
        migrationWallet
      ] = await Promise.all([
        contract.balanceTokenV1(address),
        contract.balanceStakingCompoundV1(address),
        contract.balanceStakingManualV1(address),
        contract.isMigrated(address),
        contract.totalTokenMigrated(),
        contract.tokenAddressV1(),
        contract.tokenAddressV2(),
        contract.migrationWallet()
      ]);

      // Calculate total balance
      const totalBalance = (
        parseFloat(ethers.formatEther(balanceTokenV1)) +
        parseFloat(ethers.formatEther(balanceStakingCompoundV1)) +
        parseFloat(ethers.formatEther(balanceStakingManualV1))
      ).toString();

      const data = {
        balanceTokenV1: ethers.formatEther(balanceTokenV1),
        balanceStakingCompoundV1: ethers.formatEther(balanceStakingCompoundV1),
        balanceStakingManualV1: ethers.formatEther(balanceStakingManualV1),
        totalBalance,
        isMigrated,
        totalTokenMigrated: ethers.formatEther(totalTokenMigrated),
        tokenAddressV1,
        tokenAddressV2,
        migrationWallet
      };

      setMigrationData(data);
    } catch (err) {
      console.error("Error loading migration data:", err);
      setError(err.message);
      toast.error("Failed to load migration data");
    } finally {
      setIsLoading(false);
    }
  };

  // Execute migration
  const migrate = async () => {
    if (!address || !isConnected) {
      toast.error("Please connect your wallet");
      return false;
    }

    if (migrationData.isMigrated) {
      toast.error("Already migrated");
      return false;
    }

    if (parseFloat(migrationData.totalBalance) <= 0) {
      toast.error("No V1 tokens to migrate");
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const contract = getMigrationContract(true);
      if (!contract) {
        throw new Error("Migration contract not available for this network");
      }

      // Get signer for transaction
      const ethersProvider = new ethers.BrowserProvider(walletProvider);
      const signer = await ethersProvider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Execute migration
      const tx = await contractWithSigner.migrate();
      
      toast.info("Migration transaction submitted...");
      
      // Wait for confirmation
      const receipt = await tx.wait();
      
      if (receipt.status === 1) {
        toast.success("Migration completed successfully!");
        
        // Reload migration data
        await loadMigrationData();
        return true;
      } else {
        throw new Error("Transaction failed");
      }
      
    } catch (err) {
      console.error("Migration error:", err);
      setError(err.message);
      
      if (err.code === 4001) {
        toast.error("Transaction cancelled by user");
      } else if (err.message.includes("insufficient funds")) {
        toast.error("Insufficient funds for gas fees");
      } else if (err.message.includes("already migrated")) {
        toast.error("Tokens already migrated");
      } else {
        toast.error("Migration failed: " + err.message);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Check if migration is available for current network
  const isMigrationAvailable = () => {
    const currentChainId = chainId || 56;
    const config = getAppByChainId(currentChainId);
    return !!(config.MIGRATION_ADDRESS && config.MIGRATION_ABI);
  };

  // Load data when wallet connection changes
  useEffect(() => {
    if (isConnected && address) {
      loadMigrationData();
    }
  }, [isConnected, address, chainId]);

  return {
    migrationData,
    isLoading,
    error,
    migrate,
    loadMigrationData,
    isMigrationAvailable,
    getMigrationContract
  };
};

export default useMigrationContract;